/**
 * Browser-side image compression for base64 photo uploads.
 * Resizes to a max long edge, re-encodes as JPEG at a target quality, and returns a data URL.
 * Keeps most phone photos under ~500KB after compression while staying visually sharp.
 */
export function compressImage(file, { maxDim = 1920, quality = 0.75 } = {}) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("No file provided"));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read image file"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Not a valid image"));
      img.onload = () => {
        // Scale down if either side exceeds maxDim
        let { width, height } = img;
        const scale = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        try {
          const dataUrl = canvas.toDataURL("image/jpeg", quality);
          resolve(dataUrl);
        } catch (err) {
          reject(err);
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

/** Roughly estimate the byte size of a base64 data URL. */
export function dataUrlSizeBytes(dataUrl) {
  if (!dataUrl) return 0;
  const idx = dataUrl.indexOf(",");
  const payload = idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl;
  // base64 expands by ~4/3; each char ~= 0.75 bytes decoded
  return Math.round((payload.length * 3) / 4);
}
